/**
 * Monatsübersicht der Lohnverrechnung (AP-22 Teil 2, J) — eine Zeile je
 * Mitarbeiter: Für wen liegt die Erfassung vor, wer ist fertiggestellt, was
 * fällt auf.
 *
 * Eine echte Liste und deshalb beim Listen-Standard (FilterBar, Spaltenlayout,
 * Export samt Mail). Der Monat steht in der Adresse (`?month=`), die Kacheln des
 * Dashboards führen mit ihrem Filter hierher (`?lage=COMPLETE`, AP-40 6.4).
 *
 * Der Füllstand ist gerechnet, nicht gespeichert: Der Server liefert je
 * Mitarbeiter, was er aus Tageszeilen und Arbeitszeitmodell ableitet.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, ChevronLeft, ChevronRight, Paperclip } from 'lucide-react';
import { Button } from '@symphenia/ui-components';
import { erfuelltRecht } from '@symphenia/shared-types';
import { FilterBar, filterAusAdresse } from '@/components/list/FilterBar';
import { ExportButton } from '@/components/ui/ExportButton';
import { useAuthStore } from '@/lib/auth.store';
import { bestaetige } from '@/lib/dialoge';
import { loadUiSetting } from '@/lib/ui-settings';
import type { ActiveFilter, FilterFieldDef } from '@/components/list/list.types';
import {
  fetchMonthsOverview,
  finalizeAllMonths,
  importSubmittedMonths,
  monthsOverviewExportRequest,
  type LohnmonatLage,
  type UebersichtZeile,
} from '../api/payroll.api';
import { ListTable, type ListColumn, type ListColumnState } from '../components/ListTable';

const MONATSNAMEN = [
  'Jänner', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
];

export const LAGE_TEXT: Record<LohnmonatLage, string> = {
  EMPTY: 'leer',
  IN_PROGRESS: 'in Arbeit',
  COMPLETE: 'vollständig',
  FINAL: 'fertiggestellt',
};
const LAGE_FARBE: Record<LohnmonatLage, string> = {
  EMPTY: 'bg-muted text-muted-foreground',
  IN_PROGRESS: 'bg-amber-500/15 text-amber-800 dark:text-amber-300',
  COMPLETE: 'bg-sky-500/15 text-sky-800 dark:text-sky-300',
  FINAL: 'bg-green-600/15 text-green-800 dark:text-green-300',
};
const WEG_TEXT = { TIMESHEET: 'Zeiterfassung', SLIP: 'Abgabezettel', MANUAL: 'händisch' } as const;
export const HINWEIS_TEXT: Record<string, string> = {
  NO_WORK_SCHEDULE: 'kein Arbeitszeitmodell',
  CHANGED_AFTER_FINAL: 'nach dem Fertigstellen geändert',
  SLIP_MISSING: 'Abgabezettel fehlt',
  WAITING_FOR_REPORT: 'wartet auf den Monatsbericht',
  IMPORT_OPEN: 'Übernahme offen',
  CHANGED_AFTER_EXPORT: 'nach dem BMD-Export geändert',
};

const FIXED_COLUMN: ListColumn = { key: 'name', label: 'Mitarbeiter', sortable: true };
const ALL_COLUMNS: ListColumn[] = [
  { key: 'entlohnung', label: 'Entlohnungsart', sortable: true },
  { key: 'weg', label: 'Befüllt', sortable: true },
  { key: 'erfassung', label: 'Erfassung', align: 'right', sortable: true },
  { key: 'lage', label: 'Stand', sortable: true },
  { key: 'finalizedAt', label: 'Fertiggestellt am', sortable: true },
  { key: 'exportedAt', label: 'BMD-Export am', sortable: true },
  { key: 'zettel', label: 'Abgabezettel', align: 'right' },
  { key: 'hinweise', label: 'Hinweis' },
  { key: 'normalStunden', label: 'Normalstunden', align: 'right', sortable: true },
  { key: 'mehrarbeit', label: 'Mehrarbeit', align: 'right', sortable: true },
  { key: 'ueberstunden', label: 'Überstunden', align: 'right', sortable: true },
  { key: 'zaSaldo', label: 'ZA-Saldo', align: 'right', sortable: true },
  { key: 'fehlzeitTage', label: 'Fehlzeit-Tage', align: 'right', sortable: true },
  { key: 'nlz', label: 'NLZ', align: 'right', sortable: true },
];

const SETTING_KEY = 'columnbar_layout_payroll_months';
const DEFAULT_STATE: ListColumnState = {
  columnKeys: ['weg', 'erfassung', 'lage', 'finalizedAt', 'hinweise', 'ueberstunden', 'zaSaldo'],
  columnWidths: {},
  sortKey: 'name',
  sortDir: 'asc',
};

const FILTER_FELDER: FilterFieldDef[] = [
  {
    key: 'lage',
    label: 'Stand',
    type: 'select',
    category: 'core',
    operators: ['is', 'is_not'],
    options: (Object.keys(LAGE_TEXT) as LohnmonatLage[]).map((k) => ({ value: k, label: LAGE_TEXT[k] })),
  },
  {
    key: 'weg',
    label: 'Befüllt',
    type: 'select',
    category: 'core',
    operators: ['is', 'is_not'],
    options: Object.entries(WEG_TEXT).map(([value, label]) => ({ value, label })),
  },
  {
    key: 'hinweis',
    label: 'Hinweis',
    type: 'select',
    category: 'core',
    operators: ['is', 'is_not'],
    options: Object.entries(HINWEIS_TEXT).map(([value, label]) => ({ value, label })),
  },
  { key: 'name', label: 'Mitarbeiter', type: 'text', category: 'core', operators: ['contains', 'not_contains'] },
];

function zpad(n: number): string {
  return String(n).padStart(2, '0');
}

/** Vorgabe ist der Vormonat — der ist es, der gerade abgeschlossen wird. */
function vormonat(): string {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${zpad(d.getMonth() + 1)}`;
}

function verschiebe(monat: string, um: number): string {
  const [j, m] = monat.split('-').map(Number);
  const d = new Date(j, m - 1 + um, 1);
  return `${d.getFullYear()}-${zpad(d.getMonth() + 1)}`;
}

function stunden(n: number): string {
  return n ? n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '';
}

function datum(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return `${zpad(d.getDate())}.${zpad(d.getMonth() + 1)}.${d.getFullYear()}`;
}

export default function PayrollMonthsPage() {
  const { t } = useTranslation();
  const rechte = useAuthStore((s) => s.user?.permissions);
  const darfFertigstellen = erfuelltRecht('payroll.month.finalize', rechte);

  const [params, setParams] = useSearchParams();
  const monat = /^\d{4}-\d{2}$/.test(params.get('month') ?? '') ? params.get('month')! : vormonat();
  const [jahr, mon] = monat.split('-').map(Number);
  const monatName = `${MONATSNAMEN[mon - 1]} ${jahr}`;

  // AP-40 6.4: Filter aus der Adresse (Kachel des Dashboards) — einmal beim Öffnen gelesen.
  const [adresse] = useState(() => filterAusAdresse(params, FILTER_FELDER));
  const [filterValues, setFilterValues] = useState<Record<string, ActiveFilter>>({});
  const [colState, setColState] = useState<ListColumnState>(() => loadUiSetting<ListColumnState>(SETTING_KEY, DEFAULT_STATE));
  const [rows, setRows] = useState<UebersichtZeile[]>([]);
  const [ausgeblendet, setAusgeblendet] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);

  const laden = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { zeilen, ausgeblendet: ohne } = await fetchMonthsOverview(monat);
      setRows(zeilen);
      setAusgeblendet(ohne);
    } catch {
      setError('Die Monatsübersicht konnte nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, [monat]);

  useEffect(() => {
    setMeldung(null);
    void laden();
  }, [laden]);

  const setzeMonat = (neu: string) => {
    const p = new URLSearchParams(params);
    p.set('month', neu);
    setParams(p, { replace: true });
  };

  // Gefiltert wird im Browser: Die Liste ist eine Zeile je Mitarbeiter, sie kommt ganz.
  const trifft = (ist: boolean, f: ActiveFilter) => (f.operator === 'is_not' || f.operator === 'not_contains' ? !ist : ist);
  const filteredRows = rows.filter((row) => {
    for (const [key, f] of Object.entries(filterValues)) {
      if (!f.value) continue;
      const werte = f.value.split(',');
      if (key === 'lage' && !trifft(werte.includes(row.lage), f)) return false;
      if (key === 'weg' && !trifft(werte.includes(row.weg), f)) return false;
      if (key === 'hinweis' && !trifft(werte.some((w) => row.hinweise.includes(w)), f)) return false;
      if (key === 'name' && !trifft(row.name.toLowerCase().includes(f.value.toLowerCase()), f)) return false;
    }
    return true;
  });

  const REIHE: Record<LohnmonatLage, number> = { EMPTY: 0, IN_PROGRESS: 1, COMPLETE: 2, FINAL: 3 };
  const displayRows = [...filteredRows].sort((a, b) => {
    const dir = colState.sortDir === 'asc' ? 1 : -1;
    const k = colState.sortKey;
    let cmp = 0;
    if (k === 'lage') cmp = REIHE[a.lage] - REIHE[b.lage];
    else if (k === 'weg') cmp = WEG_TEXT[a.weg].localeCompare(WEG_TEXT[b.weg], 'de');
    else if (k === 'entlohnung') cmp = (a.entlohnung ?? '').localeCompare(b.entlohnung ?? '', 'de');
    else if (k === 'erfassung') cmp = a.tageErfasst / (a.tageSoll || 1) - b.tageErfasst / (b.tageSoll || 1);
    else if (k === 'finalizedAt') cmp = (a.finalizedAt ?? '').localeCompare(b.finalizedAt ?? '');
    else if (k === 'exportedAt') cmp = (a.exportedAt ?? '').localeCompare(b.exportedAt ?? '');
    else if (k === 'normalStunden' || k === 'mehrarbeit' || k === 'ueberstunden' || k === 'zaSaldo' || k === 'fehlzeitTage' || k === 'nlz')
      cmp = a[k] - b[k];
    return cmp !== 0 ? cmp * dir : a.name.localeCompare(b.name, 'de') * (k === 'name' ? dir : 1);
  });

  const vollstaendig = rows.filter((r) => r.lage === 'COMPLETE');
  // Weg A: Abgaben, die noch nicht übernommen sind (etwa nach dem Einschalten der Lohnverrechnung).
  const uebernahmeOffen = rows.filter((r) => r.hinweise.includes('IMPORT_OPEN')).length;

  const uebernehmen = async () => {
    setError(null);
    try {
      const ergebnis = await importSubmittedMonths(monat);
      setMeldung(
        ergebnis.uebernommen
          ? `${ergebnis.uebernommen} ${ergebnis.uebernommen === 1 ? 'Monatsbericht' : 'Monatsberichte'} übernommen.`
          : 'Nichts übernommen — fertiggestellte Monate nehmen keinen Monatsbericht mehr auf.',
      );
      await laden();
    } catch {
      setError('Die Übernahme ist fehlgeschlagen.');
    }
  };

  const alleFertigstellen = async () => {
    if (
      !(await bestaetige(
        `${vollstaendig.length} ${vollstaendig.length === 1 ? 'vollständigen Monat' : 'vollständige Monate'} für ${monatName} fertigstellen? Die Zahlen werden festgehalten.`,
        { ok: 'Fertigstellen' },
      ))
    )
      return;
    setError(null);
    try {
      const ergebnis = await finalizeAllMonths(monat);
      const offen = ergebnis.ergebnisse.filter((e) => !e.ok);
      setMeldung(
        `${ergebnis.fertiggestellt} fertiggestellt` +
          (offen.length ? ` — nicht möglich bei: ${offen.map((e) => e.name).join(', ')}` : '.'),
      );
      await laden();
    } catch {
      setError('Das Fertigstellen ist fehlgeschlagen.');
    }
  };

  const filterInfo = Object.entries(filterValues)
    .filter(([, f]) => f.value)
    .map(([key, f]) => {
      const feld = FILTER_FELDER.find((x) => x.key === key);
      const nicht = f.operator === 'is_not' || f.operator === 'not_contains' ? 'nicht ' : '';
      const wert = feld?.options
        ? f.value
            .split(',')
            .map((w) => feld.options?.find((o) => o.value === w)?.label ?? w)
            .join(', ')
        : f.value;
      return `${feld?.label ?? key} ${nicht}${wert}`;
    })
    .join(' · ');

  const shownColumns = colState.columnKeys
    .map((k) => ALL_COLUMNS.find((c) => c.key === k))
    .filter((c): c is ListColumn => !!c);

  const zurErfassung = (r: UebersichtZeile) => `/payroll/entry?user=${r.userId}&month=${monat}`;

  const renderCell = (row: UebersichtZeile, col: ListColumn): React.ReactNode => {
    switch (col.key) {
      case 'entlohnung':
        return row.entlohnung ?? <span className="text-muted-foreground">Standard</span>;
      case 'weg':
        return WEG_TEXT[row.weg];
      case 'erfassung':
        return row.tageSoll ? (
          <span className="tabular-nums">
            {row.tageErfasst} von {row.tageSoll}
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        );
      case 'lage':
        return <span className={`rounded px-1.5 py-0.5 text-xs ${LAGE_FARBE[row.lage]}`}>{LAGE_TEXT[row.lage]}</span>;
      case 'finalizedAt':
        return <span className="tabular-nums">{datum(row.finalizedAt)}</span>;
      case 'exportedAt':
        return <span className="tabular-nums">{datum(row.exportedAt)}</span>;
      case 'zettel':
        return row.zettel ? (
          <span className="inline-flex items-center gap-1 tabular-nums">
            <Paperclip size={12} aria-hidden="true" />
            {row.zettel}
          </span>
        ) : (
          ''
        );
      case 'hinweise':
        return <span className="text-amber-700 dark:text-amber-300">{row.hinweise.map((h) => HINWEIS_TEXT[h] ?? h).join(', ')}</span>;
      case 'normalStunden':
        return stunden(row.normalStunden);
      case 'mehrarbeit':
        return stunden(row.mehrarbeit);
      case 'ueberstunden':
        return stunden(row.ueberstunden);
      case 'zaSaldo':
        return stunden(row.zaSaldo);
      case 'nlz':
        return stunden(row.nlz);
      case 'fehlzeitTage':
        return row.fehlzeitTage ? String(row.fehlzeitTage).replace('.', ',') : '';
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Monatsübersicht</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Für wen die Erfassung vorliegt, wer fertiggestellt ist und was noch auffällt — je Mitarbeiter eine Zeile.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center rounded-md border border-border">
            <button
              type="button"
              className="rounded-l-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
              onClick={() => setzeMonat(verschiebe(monat, -1))}
              aria-label="Vormonat"
            >
              <ChevronLeft size={16} />
            </button>
            <span className="min-w-[8.5rem] text-center text-sm font-medium tabular-nums">{monatName}</span>
            <button
              type="button"
              className="rounded-r-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
              onClick={() => setzeMonat(verschiebe(monat, 1))}
              aria-label="Folgemonat"
            >
              <ChevronRight size={16} />
            </button>
          </div>
          <ExportButton
            defaultFilename={`Lohnmonate_${monat}`}
            request={(format, orientation) =>
              monthsOverviewExportRequest(
                {
                  month: monat,
                  format,
                  orientation,
                  lage: filterValues['lage']?.operator === 'is' ? filterValues['lage'].value : undefined,
                  filterInfo: filterInfo || undefined,
                },
                // Der Mitarbeiter ist die feste erste Spalte und gehört in jeden Export.
                [FIXED_COLUMN, ...shownColumns].map((c) => ({ key: c.key, label: c.label })),
              )
            }
            mail={{
              source: 'EXPORT_PAYROLL_MONTHS',
              title: t('mailExport.titles.EXPORT_PAYROLL_MONTHS'),
              subject: `${t('mailExport.titles.EXPORT_PAYROLL_MONTHS')} ${monatName}`,
            }}
          />
          {uebernahmeOffen > 0 && erfuelltRecht('payroll.day_entry.manage', rechte) && (
            <Button size="sm" variant="outline" onClick={() => void uebernehmen()}>
              Abgegebene Monate übernehmen ({uebernahmeOffen})
            </Button>
          )}
          {darfFertigstellen && (
            <Button size="sm" disabled={vollstaendig.length === 0} onClick={() => void alleFertigstellen()}>
              <CheckCircle2 className="h-4 w-4" />
              <span className="hidden sm:inline">
                Alle vollständigen fertigstellen{vollstaendig.length ? ` (${vollstaendig.length})` : ''}
              </span>
            </Button>
          )}
        </div>
      </div>

      {meldung && (
        <div className="rounded-md border border-green-600/30 bg-green-600/10 px-4 py-2 text-sm text-foreground">{meldung}</div>
      )}

      <FilterBar
        availableFields={FILTER_FELDER}
        value={filterValues}
        onChange={setFilterValues}
        layoutKey="payroll_months"
        defaultFieldKeys={['lage', 'name']}
        adresse={adresse}
      />

      <ListTable
        allColumns={ALL_COLUMNS}
        fixedColumn={FIXED_COLUMN}
        state={colState}
        onStateChange={setColState}
        settingKey={SETTING_KEY}
        defaultState={DEFAULT_STATE}
        rows={displayRows}
        rowKey={(r) => r.userId}
        renderFixedCell={(r) => (
          <Link to={zurErfassung(r)} className="text-foreground hover:text-primary hover:underline">
            {r.name}
          </Link>
        )}
        renderCell={renderCell}
        loading={loading}
        error={error}
        emptyText="Keine Mitarbeiter in diesem Monat."
        summaryText={
          displayRows.length < rows.length
            ? `${displayRows.length} / ${rows.length} Mitarbeiter`
            : `${rows.length} Mitarbeiter · ${rows.filter((r) => r.lage === 'FINAL').length} fertiggestellt` +
              (ausgeblendet ? ` · ${ausgeblendet} ohne Lohnverrechnung ausgeblendet` : '')
        }
      />
    </div>
  );
}
